import { useEffect, useState } from "react";
import { BookOpenText, LogIn, LogOut, Route, MapPinCheck, ShieldAlert, RadioTower, FileText, StickyNote, Filter, ScrollText, KeyRound, UserCheck, UserX } from "lucide-react";
import { http } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { ExportPdfButton, TodayButton, todayISO, periodLabel } from "../components/ExportPdfButton";
import { DateInput } from "../components/DateInput";
import { SearchSelect } from "../components/SearchSelect";

export const LOG_META = {
  note: ["Note", StickyNote, "text-slate-600 bg-slate-100"], check_in: ["Prise de service", LogIn, "text-emerald-700 bg-emerald-50"],
  check_out: ["Fin de service", LogOut, "text-rose-700 bg-rose-50"], round_start: ["Ronde", Route, "text-sky-700 bg-sky-50"],
  round_scan: ["Pointeau", MapPinCheck, "text-sky-700 bg-sky-50"], round_end: ["Ronde", Route, "text-sky-700 bg-sky-50"],
  pti_alarm: ["Alarme PTI", ShieldAlert, "text-rose-700 bg-rose-100"], pti_cancel: ["Alarme PTI", ShieldAlert, "text-amber-800 bg-amber-50"],
  pti_tx: ["Télésurveillance", RadioTower, "text-indigo-700 bg-indigo-50"], report: ["Rapport", FileText, "text-amber-800 bg-amber-50"],
  instruction: ["Consigne", ScrollText, "text-emerald-700 bg-emerald-50"], key_out: ["Prêt", KeyRound, "text-amber-800 bg-amber-50"],
  key_in: ["Restitution", KeyRound, "text-emerald-700 bg-emerald-50"], visitor_in: ["Visiteur", UserCheck, "text-violet-700 bg-violet-50"],
  visitor_out: ["Visiteur", UserX, "text-slate-600 bg-slate-100"],
};
const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function MainCourante() {
  const [sites, setSites] = useState([]);
  const [f, setF] = useState({ site_id: "", start: todayISO(), end: todayISO() });
  const [items, setItems] = useState([]);
  const { lastEvent } = useRealtime();
  const load = () => http.get("/logbook", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }).then((r) => setItems(r.data));
  useEffect(() => { http.get("/sites").then((r) => setSites(r.data)); }, []);
  useEffect(() => { load(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (lastEvent?.event === "logbook_changed") load(); }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps
  const site = sites.find((s) => s.id === f.site_id);
  const sel = "border border-slate-300 rounded-md px-3 py-2 text-sm bg-white";
  return (
    <div className="p-4 md:p-8 max-w-[1800px] mx-auto" data-testid="main-courante-page">
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Main courante</h1>
          <p className="text-sm text-slate-600">{site ? site.name : "Tous vos sites"} · {periodLabel(f.start, f.end)} · {items.length} entrée(s)</p>
        </div>
        <ExportPdfButton testid="logbook-export-pdf" title={`Main courante${site ? ` — ${site.name}` : ""}`} subtitle={periodLabel(f.start, f.end)} filename={`Main_courante_${f.start || "tout"}`}
          columns={["Date et heure", "Site", "Agent", "Type", "Événement"]}
          rows={() => [...items].reverse().map((e) => [fmt(e.at), e.site_name, e.agent_name, (LOG_META[e.kind] || LOG_META.note)[0], e.text + (e.additions || []).map((a) => ` | Complément ${a.agent_name} (${fmt(a.at)}) : ${a.text}`).join("")])} />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="logbook-filters">
        <Filter className="h-4 w-4 text-slate-400" />
        <TodayButton testid="logbook-today" active={f.start === todayISO() && f.end === todayISO()} onClick={() => setF({ ...f, start: todayISO(), end: todayISO() })} />
        <SearchSelect data-testid="logbook-filter-site" className={sel} value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })}>
          <option value="">Tous les sites</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SearchSelect>
        <DateInput data-testid="logbook-start" className={sel} value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
        <span className="text-slate-400">→</span>
        <DateInput data-testid="logbook-end" className={sel} value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="logbook-list">
        {items.length === 0 && <div className="p-10 text-center text-sm text-slate-500"><BookOpenText className="h-6 w-6 mx-auto mb-2 text-slate-300" />Aucune entrée sur la période.</div>}
        {items.map((e) => {
          const [label, Icon, cls] = LOG_META[e.kind] || LOG_META.note;
          return (
            <div key={e.id} className="px-4 py-3 flex items-start gap-3" data-testid={`logbook-entry-${e.id}`}>
              <span className={`h-8 w-8 rounded-md grid place-items-center shrink-0 ${cls}`}><Icon className="h-4 w-4" /></span>
              <div className="flex-1 min-w-0">
                <div className="text-sm"><span className="font-semibold">{label}</span> — {e.text}</div>
                <div className="text-xs text-slate-500">{fmt(e.at)} · {e.agent_name} · {e.site_name}</div>
                {(e.additions || []).map((a, i) => <div key={i} className="mt-1 text-xs bg-slate-50 border-l-2 border-sky-300 pl-2 py-0.5" data-testid={`logbook-addition-${e.id}-${i}`}><b>{a.agent_name}</b> ({fmt(a.at)}) : {a.text}</div>)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
